import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useBreakpointDown } from '@/hooks/useBreakpoint';
import styles from './styles.module.scss';

export interface UserStat {
  label: string;
  value: string | number;
}

export interface ActionButton {
  label: string;
  to: string;
  icon?: string;
  variant?: 'primary' | 'outlined' | 'small';
}

export interface UserProgressCardProps {
  /** User avatar content (emoji, initial letter, or image URL) */
  avatar?: string;
  /** Whether avatar is an image URL */
  avatarIsImage?: boolean;
  /** User display name */
  userName: string;
  /** User position in leaderboard */
  position?: number | null;
  /** Position label format (e.g., "Position #1" or "Position: #1") */
  positionFormat?: 'hash' | 'colon';
  /** Not ranked text */
  notRankedText?: string;
  /** Stats to display in grid */
  stats?: UserStat[];
  /** Number of stat columns (2 or 4) */
  statColumns?: 2 | 4;
  /** Show position change indicator */
  positionChange?: number;
  /** Action button configuration */
  actionButton?: ActionButton;
  /** Whether action button is visible */
  showActionButton?: boolean;
  /** Collapsible on mobile - shows only header, stats on tap */
  collapsibleOnMobile?: boolean;
  /** Additional className */
  className?: string;
}

/**
 * Reusable user progress card component.
 * Used on TournamentDetailPage (2-col stats, position change) and LeaderboardPage (4-col stats grid).
 */
export const UserProgressCard: React.FC<UserProgressCardProps> = ({
  avatar = '👤',
  avatarIsImage = false,
  userName,
  position,
  positionFormat = 'hash',
  notRankedText = 'Not ranked yet',
  stats = [],
  statColumns = 4,
  positionChange,
  actionButton,
  showActionButton = true,
  collapsibleOnMobile = false,
  className,
}) => {
  const isMobile = useBreakpointDown('lg');
  const [isExpanded, setIsExpanded] = useState(false);

  const shouldCollapse = collapsibleOnMobile && isMobile;
  const showContent = !shouldCollapse || isExpanded;

  const handleToggle = () => {
    if (shouldCollapse) {
      setIsExpanded(!isExpanded);
    }
  };

  const positionText = position
    ? positionFormat === 'colon'
      ? `Position: #${position}`
      : `Position #${position}`
    : notRankedText;

  const getButtonClass = (variant?: string) => {
    switch (variant) {
      case 'outlined':
        return 'ls-btn ls-btn--outlined';
      case 'small':
        return 'ls-btn ls-btn--small';
      default:
        return 'ls-btn';
    }
  };

  return (
    <div className={`${styles.userProgressCard} ${className || ''}`}>
      <div
        className={`${styles.header} ${shouldCollapse ? styles.clickable : ''}`}
        onClick={handleToggle}
        role={shouldCollapse ? 'button' : undefined}
        tabIndex={shouldCollapse ? 0 : undefined}
        onKeyDown={shouldCollapse ? (e) => e.key === 'Enter' && handleToggle() : undefined}
      >
        <div className={styles.avatar}>
          {avatarIsImage ? (
            <img src={avatar} alt={userName} />
          ) : (
            avatar
          )}
        </div>
        <div className={styles.info}>
          <h3 className={styles.userName}>{userName}</h3>
          <p className={styles.position}>{positionText}</p>
        </div>
        {shouldCollapse && (
          <span className={`${styles.expandIcon} ${isExpanded ? styles.expanded : ''}`}>
            ▼
          </span>
        )}
      </div>

      {stats.length > 0 && showContent && (
        <div className={`${styles.statsSection} ${styles[`cols-${statColumns}`]} ${shouldCollapse ? styles.collapsible : ''}`}>
          {statColumns === 4 ? (
            // 4-column grid layout (LeaderboardPage style)
            <div className={styles.statsGrid}>
              {stats.map((stat, index) => (
                <div key={index} className={styles.statItem}>
                  <span className={styles.statValue}>{stat.value}</span>
                  <span className={styles.statLabel}>{stat.label}</span>
                </div>
              ))}
            </div>
          ) : (
            // 2-column flex layout (TournamentDetailPage style)
            <div className={styles.statsRow}>
              <div className={styles.pointsBlock}>
                {stats[0] && (
                  <>
                    <div className={styles.pointsValue}>{stats[0].value}</div>
                    <div className={styles.pointsLabel}>{stats[0].label}</div>
                  </>
                )}
              </div>
              {positionChange !== undefined && positionChange !== 0 && (
                <div className={`${styles.positionChange} ${positionChange < 0 ? styles.down : ''}`}>
                  {positionChange > 0 ? '▲' : '▼'}
                  <span>{Math.abs(positionChange)} positions</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {showActionButton && actionButton && showContent && (
        <div className={`${styles.actionButtons} ${shouldCollapse ? styles.collapsible : ''}`}>
          <Link
            to={actionButton.to}
            className={getButtonClass(actionButton.variant)}
            style={{ flex: 1, textAlign: 'center' }}
          >
            {actionButton.icon && `${actionButton.icon} `}{actionButton.label}
          </Link>
        </div>
      )}
    </div>
  );
};

export default UserProgressCard;
